import { createSelector } from '@reduxjs/toolkit'
import { RootState } from 'lib/core/data/reducer'
import { CustomerAddress, Customer, CustomerAddressProcessor, CustomerProcessor } from 'lib/types'
import { Slice, MagentoCustomer, MagentoCustomerAddress } from './types'
import { produce } from 'immer'

export interface CustomerProcessors {
  customerProcessor?: CustomerProcessor
  customerAddressProcessor?: CustomerAddressProcessor
}

export const getSlice = (state: RootState): Slice => {
  return state.customer.me
}

export const getMe = (processors: CustomerProcessors) => createSelector(
  getSlice,
  slice => {
    return createCustomer(slice.payload, processors)
  }
)

export const getIsSessionEndedDueInvalidToken = (state: RootState) => {
  return getSlice(state).invalidToken
}

export const getDefaultBillingAddress = (processors: CustomerProcessors) => createSelector(
  getMe(processors),
  me => {
    return me?.addresses.find(address => address.isDefaultBilling)
  }
)

export const getDefaultShippingAddress = (processors: CustomerProcessors) => createSelector(
  getMe(processors),
  me => {
    return me?.addresses.find(address => address.isDefaultShipping)
  }
)

const createCustomer = (magentoCustomer?: MagentoCustomer, processors?: CustomerProcessors): Customer | undefined => {
  if (!magentoCustomer) {
    return
  }

  const {
    // date_of_birth: dateOfBirth, // TODO: enable this for 2.3.4
    default_billing: defaultBilling,
    default_shipping: defaultShipping,
    email,
    firstname: firstName,
    gender,
    is_subscribed: isSubscribed,
    lastname: lastName
  } = magentoCustomer

  const addresses = magentoCustomer.addresses.map(address => createAddress(address, processors)).reverse()

  let customer = {
    addresses,
    // dateOfBirth,  // TODO: enable this for 2.3.4
    defaultBilling,
    defaultShipping,
    email,
    firstName,
    gender,
    isSubscribed,
    lastName
  }

  const processor = processors?.customerProcessor
  if (processor) {
    customer = produce(customer, draftAddress => processor(draftAddress, magentoCustomer))
  }

  return customer
}

const createAddress = (customerAddress: MagentoCustomerAddress, processors?: CustomerProcessors): CustomerAddress => {
  const {
    id,
    company,
    country_id: countryCode, // TODO: rename to `country_code: countryCode` for 2.3.4.
    region,
    street: streetData,
    telephone: phoneNumber,
    postcode: postalCode,
    city,
    firstname: firstName,
    lastname: lastName,
    default_shipping: defaultShipping,
    default_billing: defaultBilling
  } = customerAddress

  const { region_code: regionCode, region: regionLabel } = region
  const [street, street2] = streetData ?? []

  let address: CustomerAddress = {
    id,
    city: city ?? '',
    company: company ?? '',
    countryCode,
    firstName,
    lastName,
    postalCode,
    regionCode,
    regionLabel,
    street: street ?? '',
    street2: street2 ?? '',
    phoneNumber: phoneNumber ?? '',
    isDefaultShipping: defaultShipping ?? false,
    isDefaultBilling: defaultBilling ?? false
  }

  const processor = processors?.customerAddressProcessor
  if (processor) {
    address = produce(address, draftAddress => processor(draftAddress, customerAddress))
  }

  return address
}
